import {
  type Folder,
  type FileRetrieve,
  type FolderRetrieve,
  type MiniFile,
  type File,
  type Favorite,
  type MiniPage,
  FileTypeEnum,
} from '@openapi'
import type { GetFavoritesParams } from '@lib/types/favorites'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { RecentFile } from '@lib/types/recentFiles.types'
import { reactive, readonly } from 'vue'
import type { AppProps } from '@content-selector/appTypes'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import type { AppStore, FolderFileWithFolderId } from '@content-selector/stores/app'
// import type useAppStore from '@content-selector/stores/app'
import { sortBy } from 'lodash-es'

type FolderItemsFetcher = (
  folderId?: Folder['id'],
  options?: { justFetch: boolean; setSelectedFolder: boolean },
) => Promise<FolderRetrieve>
type FileOpener = (payload: { fileId: MiniFile['id']; pageIndex?: MiniPage['page_index']; newTab?: boolean }) => void
type FileFetcher = (payload: File['id']) => Promise<FileRetrieve>
type FileListFetcher = (params?: any) => Promise<FolderFileWithFolderId[]>
type FileDeleter = (fileId: FileRetrieve['id']) => Promise<string | void>
type FavoritesFetcher = (payload?: GetFavoritesParams) => Promise<PaginatedData<Favorite>>
type RecentFilesFetcher = () => Promise<RecentFile[]>

const getInitialState = () => ({
  fileOpener: null,
  fileDeleter: null,
  folderItemsFetcher: null,
  fileFetcher: null,
  fileListFetcher: null,
  favoritesFetcher: null,
  recentFilesFetcher: null,
  isFetchingFolderItems: false,
  isFetchingListFiles: false,
  isFetchingFile: false,
  isFetchingFavorites: false,
  isFetchingRecentFiles: false,
  isDeletingFile: false,
})

export type {
  FolderItemsFetcher,
  FileOpener,
  FileDeleter,
  FileFetcher,
  FileListFetcher,
  FavoritesFetcher,
  RecentFilesFetcher,
}

export type ApiStore = {
  state: {
    fileOpener: FileOpener | null
    fileDeleter: FileDeleter | null
    folderItemsFetcher: FolderItemsFetcher | null
    fileFetcher: FileFetcher | null
    fileListFetcher: FileListFetcher | null
    favoritesFetcher: FavoritesFetcher | null
    recentFilesFetcher: RecentFilesFetcher | null
    isFetchingFolderItems: boolean
    isFetchingListFiles: boolean
    isFetchingFile: boolean
    isFetchingFavorites: boolean
    isFetchingRecentFiles: boolean
    isDeletingFile: boolean
  }
  actions: {
    init: (props: AppProps) => void
    reset: () => void
  }
}

export default (appStore: AppStore): ApiStore => {
  const state = reactive<ApiStore['state']>(getInitialState())

  function convertFavoritesToDict(favorites: Favorite[]) {
    return favorites.reduce(
      (dict, favorite) => {
        const id = favorite.page?.id ?? favorite.file?.id ?? favorite.folder?.id
        if (id) {
          dict[id] = favorite
        }
        return dict
      },
      {} as Record<MiniPage['id'] | File['id'] | Folder['id'], Favorite>,
    )
  }

  function init(props: AppProps) {
    const { onFolderFetch, onFileOpen, onFileDelete, onFileFetch, onFilesList, onFavoritesFetch, onRecentFilesFetch } =
      props

    state.fileOpener = onFileOpen ?? null

    state.folderItemsFetcher = async function (folderId?: Folder['id']) {
      try {
        state.isFetchingFolderItems = true
        if (!onFolderFetch) throw new Error('[Content selector] - Folder fetch handler not initialized')
        const response = await onFolderFetch(folderId)

        return response
      } finally {
        state.isFetchingFolderItems = false
      }
    }

    state.fileFetcher = async function (fileId: File['id']) {
      try {
        state.isFetchingFile = true
        if (!onFileFetch) throw new Error('[Content selector] - File fetch handler not initialized')
        const response = await onFileFetch(fileId)

        return response
      } finally {
        state.isFetchingFile = false
      }
    }

    state.fileDeleter = async function (fileId: File['id']) {
      try {
        state.isDeletingFile = true
        if (!onFileDelete) throw new Error('[Content selector] - File delete handler not initialized')
        const response = await onFileDelete(fileId)

        return response
      } finally {
        state.isDeletingFile = false
      }
    }

    state.fileListFetcher = async function (params?: {
      filters?: {
        content_type: ''
        content_extension: ''
        type: [FileTypeEnum.CONTENT]
        id__in: []
      }
    }) {
      const { search, searchMetadataFilters, activeFolder, setFilteredItems } = appStore

      try {
        state.isFetchingListFiles = true
        if (!onFilesList) throw new Error('[Content selector] - File list handler not initialized')

        const payload = {
          metadataFilters: normalizeFilterParams(searchMetadataFilters.value),
          filters: {
            type: [FileTypeEnum.CONTENT],
            ...params?.filters,
          },
          search: search.value,
          original_extension__in: props.allowedOriginalExtensions,
        }

        const response = await onFilesList(payload)

        setFilteredItems(
          sortBy(
            response.filter((f) => f.status === 'viewable').map((f: any) => ({ ...f, type: 'file' })),
            [(f) => f.folder_id !== activeFolder.value?.id, 'name'],
          ),
        )

        return response
      } finally {
        state.isFetchingListFiles = false
      }
    }

    state.favoritesFetcher = async function (payload?: GetFavoritesParams) {
      const { setFavorites } = appStore
      try {
        state.isFetchingFavorites = true
        if (!onFavoritesFetch) throw new Error('[Content selector] - Favorites fetch handler not initialized')
        const response = await onFavoritesFetch(payload)

        setFavorites(convertFavoritesToDict(response.results))

        return response
      } finally {
        state.isFetchingFavorites = false
      }
    }

    state.recentFilesFetcher = async function () {
      const { setRecentFiles } = appStore
      try {
        state.isFetchingRecentFiles = true
        if (!onRecentFilesFetch) throw new Error('[Content selector] - Recent files fetch handler not initialized')
        const response = await onRecentFilesFetch()

        setRecentFiles(response)

        return response
      } finally {
        state.isFetchingRecentFiles = false
      }
    }
  }

  return {
    state: readonly(state),
    actions: {
      init,
      reset: () => Object.assign(state, getInitialState()),
    },
  }
}
